import { useContext } from "react";

import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { DateOnlyReviewedCareElement } from "@/Flowsheets/Models/CareElements/DateOnlyReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

interface CareElementDateOnlyDateReviewedProps {
    isEditable: boolean;
    label: string;
    side?: "row" | "column";
    element: DateOnlyReviewedCareElement;
    onChange: (element: DateOnlyReviewedCareElement) => void;
}

/**
 * Decimal input for the date reviewed care element.
 * @param props - The properties for the component.
 * @property {DateOnlyReviewedCareElement} props.element - The care element to be displayed.
 * @property {function} props.onChange - Callback function to handle changes to the care element.
 * @property {boolean} props.isEditable - Indicates if the care element is editable.
 * @property {string} props.label - The label for the care element.
 * @property {string} props.side - The side on which the care element is displayed (row or column).
 * @returns JSX.Element
 */
export default function CareElementDateOnlyDateReviewed(props: Readonly<CareElementDateOnlyDateReviewedProps>): JSX.Element {
    const { element, onChange, isEditable, label, side = "row" } = props;
    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
            isInputControl
        >
            {isEditable ? (
                <TextboxControlWithDatePicker
                    id={`date-${element.Name}`}
                    key={`date-${element.Name}`}
                    expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                    dateValue={element?.ValueWithPrecision?.value}
                    textValue={element?.ValueWithPrecision?.text}
                    onChange={value => {
                        const isValid = value.value !== null && value.status === LifeStageDateStatusEnum.Valid && value.isDayPrecision;

                        const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;

                        let dateToUse = null;

                        if (isValid) {
                            dateToUse = shouldAutoFillDate
                                ? encounterDateTimeWithPrecision
                                : element.DateReviewedWithPrecision;
                        }
                        else {
                            value.value = null;
                        }

                        onChange({ ...element, Value: value.value, ValueWithPrecision: value, DateReviewedWithPrecision: dateToUse });
                    }}
                    maxDate={encounterDateTimeWithPrecision.value}
                    width={120}
                    dataCy={`date-${element.Name}`}
                />
            ) : (
                element.Value && <p>{element.Value}</p>
            )
            }
        </CareElementDateReviewedRow>
    );
}
